import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { cn } from "./lib/cn";

export interface SlideshowTitleCardProps {
  title: string;
  dateLine?: string | null;
  /** Already-resolved background image URL (caller applies cdnUrl). */
  coverUrl?: string | null;
  /** Optional extra content under the title (guest: QR + idle hint). */
  qrSlot?: ReactNode;
  className?: string;
}

/**
 * The canonical Sahne title card — couple name over a blurred cover-bleed with
 * a vignette + film grain (falls back to a flat dark surface with no cover).
 * Typography scales to its container via container-query units (`cqmin`), so the
 * SAME component reads full-screen for the guest projector and miniature inside
 * the Reji monitor. Used as the idle/attract screen and the every-7th-slide
 * interstitial.
 */
export function SlideshowTitleCard({
  title,
  dateLine,
  coverUrl,
  qrSlot,
  className,
}: SlideshowTitleCardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "absolute inset-0 z-10 flex flex-col items-center justify-center text-center overflow-hidden bg-[#0b0a09] [container-type:size]",
        className,
      )}
    >
      {coverUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center blur-2xl scale-110 opacity-25 ss-ambient"
          style={{ backgroundImage: `url(${coverUrl})` }}
          aria-hidden
        />
      )}
      <div className="absolute inset-0 bg-[#0b0a09]/70" aria-hidden />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse at center, transparent 38%, rgba(0,0,0,0.85) 100%)" }}
        aria-hidden
      />

      <div className="relative flex flex-col items-center gap-[3cqmin] max-w-2xl px-[6cqmin]">
        <span className="flex items-center gap-[0.6em] text-primary/90 uppercase tracking-[0.35em] text-[clamp(0.5rem,1.6cqmin,0.75rem)] font-semibold">
          <Sparkles className="size-[1.2em]" />
          {t("slideshow.title")}
        </span>
        <h1 className="font-serif italic text-white text-[clamp(1.4rem,7cqmin,4.5rem)] leading-[1.05] drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]">
          {title}
        </h1>
        {dateLine && (
          <p className="text-white/65 uppercase tracking-[0.25em] text-[clamp(0.6rem,2cqmin,1rem)]">
            {dateLine}
          </p>
        )}
        {qrSlot}
      </div>

      <div className="ss-grain absolute inset-0 pointer-events-none" aria-hidden />
    </div>
  );
}
